import { useRef, useState } from 'react';
import { useApolloClient, useMutation } from '@apollo/client';
import { trackAccount501c3Rejected, trackAccount501c3UploadFailed, trackAccount501c3Uploaded } from '../../analytics';
import {
  rescueDocumentOverviewQuery,
  rescueGet501c3UploadUrlQuery,
  rescueSubmitExemptionDocumentMutation,
  type RescueDocumentType,
  type RescueGet501c3UploadUrlData,
  type RescueSubmitExemptionDocumentData,
  type RescueSubmitExemptionDocumentVariables,
} from '../../lib/documentQueries';

const MAX_FILE_BYTES = 20 * 1024 * 1024;

// MIME type sent on the S3 PUT. Must match what the presigned URL was signed with
// (`APPLICATION_PDF` → `application/pdf`) or S3 rejects the upload.
const PDF_CONTENT_TYPE = 'application/pdf';
const UPLOAD_CONTENT_TYPE = 'APPLICATION_PDF';

/**
 * Coarse bucket for an upload failure, so the analytics event stays a small enumerable set rather
 * than free-text error strings.
 */
function categorizeUploadError(err: unknown): string {
  const message = err instanceof Error ? err.message.toLowerCase() : '';
  if (message.includes('failed to fetch') || message.includes('networkerror')) {
    return 'network_error';
  }
  if (message.includes('forbidden') || message.includes('403')) {
    return 'forbidden';
  }
  return 'unknown';
}

/**
 * The three-step upload handoff, for whichever document is being filed.
 *
 * 1. ask for a short-lived presigned PUT URL, 2. PUT the bytes straight to S3, 3. hand the object
 * key over to be filed. The URL signs a location, so the same one serves any document type — the
 * type and jurisdiction matter at filing, which is why they are passed to the submit and not to the
 * signing.
 *
 * One hook per card rather than per row: the file input is a ref, and a row-level hook would mean
 * one hidden input per jurisdiction.
 */
export function useExemptionDocumentUpload(onUploaded?: (documentType: RescueDocumentType) => void) {
  const client = useApolloClient();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const targetRef = useRef<{ documentType: RescueDocumentType; state: string | null } | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);
  const [uploadingFor, setUploadingFor] = useState<string | null>(null);

  const [submitDocument] = useMutation<RescueSubmitExemptionDocumentData, RescueSubmitExemptionDocumentVariables>(
    rescueSubmitExemptionDocumentMutation,
    {
      // Filing supersedes whatever row was current, so the overview is refetched rather than merged
      // into — Apollo can't know the new row replaces an old one at a different id.
      refetchQueries: [{ query: rescueDocumentOverviewQuery }],
      awaitRefetchQueries: true,
    },
  );

  /** Identifies which row is mid-upload, so only that one shows a spinner. */
  function keyFor(documentType: RescueDocumentType, state: string | null) {
    return `${documentType}:${state ?? ''}`;
  }

  function pickFileFor(documentType: RescueDocumentType, state: string | null) {
    targetRef.current = { documentType, state };
    setFileError(null);
    fileInputRef.current?.click();
  }

  async function handleFileSelected(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    // Allow re-selecting the same file after a rejection.
    e.target.value = '';
    const target = targetRef.current;
    if (!file || !target) {
      return;
    }

    const isPdf = file.type === PDF_CONTENT_TYPE || file.name.toLowerCase().endsWith('.pdf');
    if (!isPdf) {
      trackAccount501c3Rejected('not_pdf');
      setFileError('Please upload a PDF file.');
      return;
    }
    if (file.size > MAX_FILE_BYTES) {
      trackAccount501c3Rejected('size_exceeded');
      setFileError('That file is over 20MB — please upload a smaller PDF.');
      return;
    }

    setUploadingFor(keyFor(target.documentType, target.state));
    try {
      // `no-cache` so we never replay a previously-cached URL whose expiration has elapsed.
      const { data: urlData, error } = await client.query<RescueGet501c3UploadUrlData>({
        query: rescueGet501c3UploadUrlQuery,
        variables: { input: { contentType: UPLOAD_CONTENT_TYPE } },
        fetchPolicy: 'no-cache',
      });
      if (error) {
        throw error;
      }
      const upload = urlData?.rescueGet501c3UploadUrl;
      if (!upload?.url) {
        setFileError('Could not start the upload — please try again.');
        trackAccount501c3UploadFailed('empty_upload_url');
        return;
      }

      // The signed URL signs only `host`, so the request must not carry an `x-amz-*` header the
      // signature doesn't cover. `Content-Type` isn't `x-amz-*`, so S3 matches it against the
      // signed content type.
      const putRes = await fetch(upload.url, {
        method: 'PUT',
        headers: { 'Content-Type': PDF_CONTENT_TYPE },
        body: file,
      });
      if (!putRes.ok) {
        setFileError(`Upload failed (HTTP ${putRes.status}) — please try again.`);
        trackAccount501c3UploadFailed(`s3_put_status_${putRes.status}`);
        return;
      }

      await submitDocument({
        variables: {
          input: {
            uploadedFileKey: upload.key,
            fileName: file.name,
            documentType: target.documentType,
            state: target.state,
          },
        },
      });

      trackAccount501c3Uploaded(file.size);
      onUploaded?.(target.documentType);
    } catch (err) {
      setFileError(err instanceof Error ? err.message : 'Upload failed');
      trackAccount501c3UploadFailed(categorizeUploadError(err));
    } finally {
      setUploadingFor(null);
      targetRef.current = null;
    }
  }

  return { fileInputRef, handleFileSelected, pickFileFor, fileError, uploadingFor, keyFor, PDF_CONTENT_TYPE };
}
